import React from 'react';
import { getLocalTime } from '../../utilities/time';
import { useCurrentUser } from '../../providers/UserProvider';
import { Text } from '../typography';
import styles from './ActionItemComment.module.scss';
import {
  priorityStringLookup,
  statusStringLookup,
} from './action_item_utilities';
import { useTranslation } from 'react-i18next';
import Label from './Label';
import { eventTypeLookup } from '../../pages/analytics/utilities';
import { useWorkspacePermissions } from '../../providers/WorkspacePermissionsProvider';
import { useWorkspace } from '../../providers/WorkspaceProvider';
import { getRoute, paths } from '../../constants/strings';

const generateAssetLink = ({ id, workspaceId, assets }) => {
  const asset = assets?.find((a) => a.id === id) ?? {};
  return (
    <a href={`/workspace/${workspaceId}/asset/${id}`}>
      {asset?.make || asset?.model || asset?.unitNumber
        ? `${asset.make} ${asset.model} ${asset.unitNumber}`
        : 'an Asset'}
    </a>
  );
};

const getChangeDescription = (
  event,
  t,
  allUsers,
  workspaceVehicles,
  isCorrective = false,
  isMilitary,
) => {
  const {
    creator,
    eventType,
    information: { Delta: delta },
  } = event;

  const assignee =
    allUsers?.find((u) => u.id === delta?.assigneeId?.new) ?? null;
  const assigneeFullName = !!assignee
    ? `${assignee?.firstName} ${assignee?.lastName}`
    : 'a new user';
  const creatorFullName = `${creator.firstName} ${creator.lastName}`;
  const creatorLink = <a href={`/user/${creator.id}`}>{creatorFullName}</a>;
  const assigneeLink = (
    <a href={`/user/${delta?.assigneeId?.new}`}>{assigneeFullName}</a>
  );
  const inspectionLink = (
    <a
      href={getRoute(
        event.workspaceId,
        paths.assessment,
        delta?.inspectionId?.old,
      )}
    >
      assessment
    </a>
  );

  const participantId =
    delta?.participants?.new?.userId || delta?.participants?.old?.userId;

  const participant = allUsers?.find((u) => u.id === participantId) ?? null;

  const participantFullName = !!participant
    ? `${participant?.firstName} ${participant?.lastName}`
    : 'a new user';
  const participantLink = (
    <a href={`/user/${participantId}`}>{participantFullName}</a>
  );

  if (!delta || Object.keys(delta).length !== 1) {
    if (eventType === eventTypeLookup.createActionItem) {
      return (
        <div className={styles.activityString}>
          {creatorLink} created this action item on{' '}
          {getLocalTime(event.dateCreated).format(
            isMilitary
              ? 'dddd MMMM Do YYYY, HH:mm'
              : 'dddd MMMM Do YYYY, h:mm a',
          )}
        </div>
      );
    }
    if (eventType === eventTypeLookup.createActionItemChecklist) {
      return (
        <div className={styles.activityString}>
          {creatorLink} added checklist item{' '}
          <strong>{event.information?.ChecklistDescription}</strong>
        </div>
      );
    } else {
      return null;
    }
  }
  const checklistDescription = event.information?.ChecklistDescription || null;

  const [key] = Object.keys(delta);

  switch (key) {
    case 'isArchived': {
      return (
        <div className={styles.activityString}>
          {creatorLink || event?.name || 'User'} deleted checklist item{' '}
          <strong> {checklistDescription}.</strong>
        </div>
      );
    }
    case 'items': {
      const delta = event.information.Delta?.items || {};
      const oldItems = delta.old || [];
      const newItems = delta.new || [];
      const changedFields = [];
      const oldIds = new Set(oldItems.map((item) => item.id));
      const createdItems = newItems.filter((item) => !oldIds.has(item.id));

      createdItems.forEach((item) => {
        changedFields.push(
          <span
            style={{ fontFamily: 'eina03regular' }}
            key={`created-${item.id}`}
          >
            <strong>"{item.value}"</strong>
          </span>,
        );
      });
      const updatedItems = [];
      newItems.forEach((newItem) => {
        const oldItem = oldItems.find((item) => item.id === newItem.id);
        if (oldItem) {
          let changed = false;
          if (oldItem.value !== newItem.value) {
            changedFields.push(
              <span
                style={{ fontFamily: 'eina03regular' }}
                key={`val-${newItem.id}`}
              >
                from <strong>"{oldItem.value}"</strong> to{' '}
                <strong>"{newItem.value}"</strong>
              </span>,
            );
            changed = true;
          }
          if (oldItem.status !== newItem.status) {
            changedFields.push(
              <span
                style={{ fontFamily: 'eina03regular' }}
                key={`status-${newItem.id}`}
              >
                <strong>{newItem.value}</strong> status from{' '}
                <strong>
                  {t(statusStringLookup[oldItem.status]?.title) ||
                    oldItem.status}
                </strong>{' '}
                to{' '}
                <strong>
                  {t(statusStringLookup[newItem.status]?.title) ||
                    newItem.status}
                </strong>
              </span>,
            );
            changed = true;
          }
          if (changed) updatedItems.push(newItem);
        }
      });

      if (changedFields.length === 0) return null;

      let actionPrefix = '';
      if (createdItems.length) {
        actionPrefix = 'created';
      } else {
        actionPrefix = 'updated';
      }

      const joined = changedFields.reduce((prev, curr, idx) =>
        idx ? [prev, ' | ', curr] : curr,
      );

      return (
        <div className={styles.activityString}>
          {creatorLink || event?.name || 'User'} {actionPrefix} checklist
          subitem {joined}
        </div>
      );
    }
    case 'inspectionId':
      return (
        <div>
          <div className={styles.activityString}>
            {creatorLink || event?.name || 'User'} transferred Action Item from{' '}
            {inspectionLink}.
          </div>
        </div>
      );
    case 'labels':
      return delta.labels.new ? (
        <div className={styles.labelString}>
          <div>{creatorLink} added the label </div>
          <Label name={delta.labels.new.name} color={delta.labels.new.color} />
        </div>
      ) : (
        <div className={styles.labelString}>
          <div>{creatorLink} removed the label </div>
          <Label name={delta.labels.old.name} color={delta.labels.old.color} />
        </div>
      );
    case 'outcome':
      return (
        <div className={styles.activityString}>
          {creatorLink} updated the action taken.
        </div>
      );
    case 'locked':
      return (
        <div className={styles.activityString}>
          {creatorLink} locked this action item.
        </div>
      );
    case 'assigneeId':
      return delta.assigneeId.new ? (
        <div className={styles.activityString}>
          {creatorLink} assigned{' '}
          {event.eventType === eventTypeLookup.updateActionItemChecklist ? (
            <>
              <strong>{checklistDescription}</strong>
            </>
          ) : (
            'this'
          )}{' '}
          to {assigneeLink}.
        </div>
      ) : (
        <div className={styles.activityString}>
          {creatorLink} unassigned this action item
        </div>
      );
    case 'status':
      return (
        <div className={styles.activityString}>
          {creatorLink} updated{' '}
          {!!checklistDescription ? (
            <strong>{checklistDescription}</strong>
          ) : (
            'Action Item'
          )}{' '}
          status from{' '}
          <strong>{t(statusStringLookup[delta.status.old].title)}</strong> to{' '}
          <strong>{t(statusStringLookup[delta.status.new].title)}</strong>.
        </div>
      );
    case 'priority':
      return (
        <div className={styles.activityString}>
          {creatorLink} updated the priority from{' '}
          <strong>{t(priorityStringLookup[delta.priority.old].name)}</strong> to{' '}
          <strong>{t(priorityStringLookup[delta.priority.new].name)}</strong>.
        </div>
      );
    case 'title':
      return (
        <div className={styles.activityString}>
          {creatorLink} updated the title from{' '}
          <strong>"{delta.title.old}"</strong> to{' '}
          <strong>"{delta.title.new}"</strong>.
        </div>
      );
    case 'description':
      return (
        <div className={styles.activityString}>
          {creatorLink} updated the{' '}
          {event.eventType === eventTypeLookup.updateActionItemChecklist ? (
            <>
              checklist <strong>{delta.description.old}</strong> to{' '}
              <strong>{delta.description.new}</strong>
            </>
          ) : (
            'description.'
          )}{' '}
        </div>
      );
    case 'vehicleId':
      return delta.vehicleId.new ? (
        <div className={styles.activityString}>
          {creatorLink} linked{' '}
          {generateAssetLink({
            id: delta.vehicleId.new,
            workspaceId: event.workspaceId,
            assets: workspaceVehicles,
          })}
          .
        </div>
      ) : (
        <div className={styles.activityString}>
          {creatorLink} unlinked{' '}
          {generateAssetLink({
            id: delta.vehicleId.old,
            workspaceId: event.workspaceId,
            assets: workspaceVehicles,
          })}
          .
        </div>
      );
    case 'dueDate':
      return delta.dueDate.old && !delta.dueDate.new ? (
        <div className={styles.activityString}>
          {creatorLink} cleared the due date.
        </div>
      ) : (
        <div className={styles.activityString}>
          {creatorLink} set{' '}
          {!!event.information?.ChecklistDescription ? (
            <>
              <strong>{checklistDescription}</strong>
            </>
          ) : (
            'the'
          )}{' '}
          Due Date to{' '}
          <strong>
            {getLocalTime(delta.dueDate.new).format('dddd, MMMM Do YYYY')}
          </strong>
          .
        </div>
      );
    case 'participants':
      const string = !!isCorrective ? 'delegate' : 'participant';
      return delta?.participants?.new ? (
        <div className={styles.activityString}>
          {creatorLink} added{' '}
          {creator?.id === participantId ? 'themselves' : participantLink} as a{' '}
          {string}.
        </div>
      ) : (
        <div className={styles.activityString}>
          {creatorLink} removed{' '}
          {creator?.id === participantId ? 'themselves' : participantLink} as a{' '}
          {string}.
        </div>
      );
    case 'attachments':
      return delta?.attachments?.new ? (
        <div className={styles.activityString}>
          {creatorLink} added an attachment.
        </div>
      ) : (
        <div className={styles.activityString}>
          {creatorLink} removed an attachment.
        </div>
      );
    case 'private':
      return delta?.private?.new ? (
        <div className={styles.activityString}>
          {creatorLink} marked this as private
        </div>
      ) : (
        <div className={styles.activityString}>
          {creatorLink} marked this as public
        </div>
      );
    default:
      return null;
  }
};

export default function ActionItemHistory({ event, isCorrective }) {
  const { t } = useTranslation();
  const { allUsers } = useWorkspacePermissions();
  const { workspaceVehicles } = useWorkspace();
  const { user } = useCurrentUser();
  const isMilitary = user?.timeSetting === 'military';
  const change = getChangeDescription(
    event,
    t,
    allUsers,
    workspaceVehicles,
    isCorrective,
    isMilitary,
  );

  return !!change ? (
    <div className={styles.container}>
      <img
        className={styles.avatar}
        src={event.creator.avatarUrl}
        alt={`${event.creator.firstName} ${event.creator.lastName}s avatar`}
      />
      <div className={styles.rightContainer}>
        <div className={styles.header}>
          <Text
            size="md"
            weight="bold"
            noMargin
          >{`${event.creator.firstName} ${event.creator.lastName}`}</Text>
          <Text size="sm" noMargin color="secondary">
            {getLocalTime(event.dateCreated).format(
              isMilitary
                ? 'dddd MMMM Do YYYY HH:mm'
                : 'dddd MMMM Do YYYY h:mma',
            )}
          </Text>
        </div>
        {change}
      </div>
    </div>
  ) : null;
}
